import React, { useState, useEffect } from 'react';
import { Loader2, MessageCircle } from 'lucide-react';

interface TelegramVisualButtonProps {
  text: string;
  onClick: () => void;
  color?: string;
  textColor?: string;
  visible?: boolean;
  disabled?: boolean;
  showProgress?: boolean;
  className?: string;
  style?: React.CSSProperties;
  selected?: boolean;
  showIcon?: boolean;
  hideOnKeyboard?: boolean; // Se false, il pulsante rimane visibile anche con tastiera aperta
}

/**
 * Componente che simula visivamente il MainButton di Telegram.
 * Versione semplificata senza dipendenze shadcn/ui e conflitti di stile.
 */
export function TelegramVisualButton({
  text,
  onClick,
  visible = true,
  disabled = false,
  color = '#2565eb',
  textColor = '#ffffff',
  showProgress = false,
  className = '',
  style = {},
  selected = false,
  showIcon = false,
  hideOnKeyboard = true
}: TelegramVisualButtonProps) {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  // Rileva apertura/chiusura tastiera virtuale
  useEffect(() => {
    let initialViewportHeight = window.visualViewport?.height || window.innerHeight;

    const handleViewportChange = () => {
      const currentViewportHeight = window.visualViewport?.height || window.innerHeight;
      const heightDifference = initialViewportHeight - currentViewportHeight;
      const keyboardThreshold = 100;
      const keyboardIsOpen = heightDifference > keyboardThreshold;
      setIsKeyboardOpen(keyboardIsOpen);
    };

    // Detection preventiva su focus/blur di input/textarea
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true')) {
        setTimeout(() => setIsKeyboardOpen(true), 50);
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true')) {
        setTimeout(() => {
          const currentViewportHeight = window.visualViewport?.height || window.innerHeight;
          const heightDifference = initialViewportHeight - currentViewportHeight;
          const keyboardIsOpen = heightDifference > 100;
          setIsKeyboardOpen(keyboardIsOpen);
        }, 100);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      document.addEventListener('focusin', handleFocusIn);
      document.addEventListener('focusout', handleFocusOut);

      return () => {
        window.visualViewport?.removeEventListener('resize', handleViewportChange);
        document.removeEventListener('focusin', handleFocusIn);
        document.removeEventListener('focusout', handleFocusOut);
      };
    } else {
      window.addEventListener('resize', handleViewportChange);
      document.addEventListener('focusin', handleFocusIn);
      document.addEventListener('focusout', handleFocusOut);

      return () => {
        window.removeEventListener('resize', handleViewportChange);
        document.removeEventListener('focusin', handleFocusIn);
        document.removeEventListener('focusout', handleFocusOut);
      };
    }
  }, []);

  // Assicura che il colore sia sempre #2565eb se non specificato
  const finalColor = color || '#2565eb';

  console.log('[TelegramVisualButton] Rendering pulsante sempre visibile:', {
    visible,
    text,
    disabled,
    showProgress,
    onClick: typeof onClick
  });

  if (!visible || (hideOnKeyboard && isKeyboardOpen)) {
    console.log('[TelegramVisualButton] Non visibile, visible=false o tastiera aperta');
    return null;
  }

  // Stili inline puliti senza conflitti
  const buttonStyles: React.CSSProperties = {
    width: '100%',
    height: '48px',
    borderRadius: '12px',
    border: 'none',
    fontWeight: '500',
    fontSize: '16px',
    cursor: disabled || showProgress ? 'not-allowed' : 'pointer',
    transition: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    background: disabled && finalColor !== '#22c55e'
      ? '#6b7280'
      : (finalColor === '#22c55e'
          ? finalColor
          : 'linear-gradient(to right, #2563eb, #9333ea)'),
    color: disabled ? '#9ca3af' : textColor,
    opacity: disabled ? 0.6 : 1,
    transform: 'none',
    ...style
  };

  const containerStyles: React.CSSProperties = {
    position: 'fixed',
    bottom: '0',
    left: '0',
    right: '0',
    zIndex: 20,
    padding: '16px 16px 32px 16px',
    backgroundColor: 'var(--tg-bg-color)',
    height: '96px' // Limita l'altezza del container
  };

  return (
    <div style={containerStyles} className={className}>
      <button
        onClick={() => {
          console.log('[TelegramVisualButton] Button clicked!', { text });
          
          // Aggiungi feedback tattile se disponibile
          try {
            console.log('[TelegramVisualButton] === TENTATIVO FEEDBACK TATTILE ===');
            console.log('[TelegramVisualButton] window.Telegram:', !!window.Telegram);
            console.log('[TelegramVisualButton] window.Telegram?.WebApp:', !!window.Telegram?.WebApp);
            console.log('[TelegramVisualButton] HapticFeedback:', !!window.Telegram?.WebApp?.HapticFeedback);
            console.log('[TelegramVisualButton] navigator.vibrate:', !!navigator.vibrate);
            
            if (window.Telegram?.WebApp?.HapticFeedback) {
              console.log('[TelegramVisualButton] ✅ Attivando feedback tattile Telegram...');
              window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
              console.log('[TelegramVisualButton] ✅ Feedback tattile Telegram attivato!');
            } else if (navigator.vibrate) {
              console.log('[TelegramVisualButton] ✅ Fallback a vibrazione browser...');
              const vibrateResult = navigator.vibrate(50); // 50ms di vibrazione
              console.log('[TelegramVisualButton] ✅ Vibrazione browser risultato:', vibrateResult);
            } else {
              console.log('[TelegramVisualButton] ❌ Nessun metodo di feedback tattile disponibile');
            }
          } catch (error) {
            console.error('[TelegramVisualButton] ❌ Errore feedback tattile:', error);
          }
          
          // Gestisci onClick con protezione dagli errori
          try {
            if (typeof onClick === 'function') {
              onClick();
            } else {
              console.warn('[TelegramVisualButton] onClick non è una funzione:', typeof onClick);
            }
          } catch (error) {
            console.error('[TelegramVisualButton] ❌ Errore durante esecuzione onClick:', error);
            // Non bloccare l'interfaccia, ma logga l'errore
          }
        }}
        disabled={disabled || showProgress}
        style={buttonStyles}
      >
        {showProgress ? (
          <>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            <span>{text}</span>
          </>
        ) : (
          <>
            <span>{text}</span>
            {showIcon && <MessageCircle size={18} />}
          </>
        )}
      </button>
    </div>
  );
}

export default TelegramVisualButton;